import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, GraduationCap, Droplets, Bot, Zap, Check, Building2, Briefcase, Users, MessageSquare, ClipboardList, FileCheck2, RefreshCw } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/Brand";
import { SERVICES } from "@/lib/siteContent";

const ICONS = { "privacy-condomini": ShieldCheck, "formazione-amministratori": GraduationCap, "analisi-acqua": Droplets, "assistente-ai-comunicazioni": Bot, "consulenza-energetica": Zap };

const Hero = () => (
  <section className="relative overflow-hidden border-b border-slate-200 bg-white">
    <div className="grid-bg pointer-events-none absolute inset-0" />
    <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-12 px-6 pb-20 pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:pt-24">
      <div>
        <p className="fade-up text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Servizi per condomini e amministratori</p>
        <h1 className="fade-up mt-4 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" style={{ animationDelay: "60ms" }}>
          Un solo partner<br />per tutto il condominio.
        </h1>
        <p className="fade-up mt-6 max-w-lg text-base leading-relaxed text-slate-600" style={{ animationDelay: "120ms" }}>
          Adeguamento privacy, formazione per amministratori, analisi dell'acqua negli impianti condominiali, consulenza energetica per luce e gas e un assistente di intelligenza artificiale per le comunicazioni: affianchiamo gli studi di amministrazione negli adempimenti, con documenti chiari e controlli programmati.
        </p>
        <div className="fade-up mt-10 flex flex-wrap gap-3" style={{ animationDelay: "180ms" }}>
          <Link to="/contatti" data-testid="home-hero-contatti-button" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-3.5 font-medium text-white transition-colors hover:bg-blue-600">
            Richiedi una consulenza <ArrowRight className="h-4 w-4" />
          </Link>
          <a href="#servizi" data-testid="home-hero-servizi-button" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-3.5 font-medium text-slate-800 transition-colors hover:border-slate-900">
            Scopri i servizi
          </a>
        </div>
      </div>
      <div className="fade-up grid min-w-0 grid-cols-1 gap-3" style={{ animationDelay: "240ms" }}>
        {SERVICES.map((s) => {
          const Icon = ICONS[s.slug];
          return (
            <Link key={s.slug} to={`/servizi/${s.slug}`} data-testid={`home-hero-tile-${s.slug}`} className="group flex min-w-0 items-center gap-4 rounded-2xl border border-slate-200 bg-white/90 p-3 pr-5 shadow-sm backdrop-blur transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lg">
              <img src={s.image} alt="" className="h-20 w-24 shrink-0 rounded-xl object-cover" />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 font-heading font-semibold text-slate-900"><Icon className="h-4 w-4 text-blue-600" /> {s.title}</p>
                <p className="mt-1 truncate font-mono text-[11px] text-slate-500">{s.eyebrow}</p>
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-400 transition-colors group-hover:text-blue-600" />
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const ServiceDeep = ({ s, i }) => {
  const Icon = ICONS[s.slug];
  return (
    <div data-testid={`home-deep-${s.slug}`} className="grid gap-10 py-14 lg:grid-cols-2 lg:items-center">
      <img src={s.image} alt={s.title} className={`h-80 w-full rounded-2xl object-cover ${i % 2 ? "lg:order-2" : ""}`} />
      <div>
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-blue-600"><Icon className="h-4 w-4" /> {s.eyebrow}</p>
        <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{s.title}</h3>
        <p className="mt-3 leading-relaxed text-slate-600">{s.intro}</p>
        <ul className="mt-6 space-y-3">
          {s.points.slice(0, 4).map(([t, d]) => (
            <li key={t} className="flex gap-3">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-50 text-emerald-600"><Check className="h-3 w-3" /></span>
              <span className="text-sm text-slate-700"><span className="font-semibold text-slate-900">{t}.</span> {d}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/servizi/${s.slug}`} data-testid={`home-deep-more-${s.slug}`} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-600">Dettagli del servizio <ArrowRight className="h-4 w-4" /></Link>
          <Link to={s.cta.to} data-testid={`home-deep-cta-${s.slug}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-800 transition-colors hover:border-slate-900">{s.cta.label}</Link>
        </div>
      </div>
    </div>
  );
};

const DeepSections = () => (
  <section id="servizi" className="scroll-mt-20 border-y border-slate-200 bg-white" data-testid="home-services">
    <div className="mx-auto max-w-6xl px-6 pt-20">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Servizi</p>
      <h2 className="mt-2 max-w-xl font-heading text-3xl font-bold tracking-tight text-slate-900">Cinque aree di competenza, un unico interlocutore.</h2>
    </div>
    <div className="mx-auto max-w-6xl divide-y divide-slate-200 px-6 pb-6">
      {SERVICES.map((s, i) => <ServiceDeep key={s.slug} s={s} i={i} />)}
    </div>
  </section>
);

const AUDIENCE = [
  [Briefcase, "Amministratori di condominio", "Documenti pronti per ogni condominio gestito, formazione periodica e un referente per i controlli tecnici."],
  [Building2, "Condomìni", "Parti comuni gestite nel rispetto delle norme su dati personali, videosorveglianza, sicurezza e qualità dell'acqua."],
  [Users, "Condòmini e residenti", "Informative sempre consultabili online e maggiore tutela della salute e della riservatezza."],
];

const Audience = () => (
  <section className="mx-auto max-w-6xl px-6 py-20" data-testid="home-audience">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Per chi lavoriamo</p>
    <h2 className="mt-2 max-w-xl font-heading text-3xl font-bold tracking-tight text-slate-900">Al fianco di chi vive e gestisce il condominio.</h2>
    <div className="mt-10 grid gap-6 md:grid-cols-3">
      {AUDIENCE.map(([Icon, t, d]) => (
        <div key={t} className="border-t-2 border-slate-900 pt-5">
          <Icon className="h-5 w-5 text-blue-600" />
          <p className="mt-3 font-heading font-semibold text-slate-900">{t}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const STEPS = [
  [MessageSquare, "Primo contatto", "Ci racconti il condominio o lo studio e i servizi di cui hai bisogno."],
  [ClipboardList, "Analisi", "Verifichiamo la situazione attuale: documenti, impianti, esigenze formative."],
  [FileCheck2, "Intervento", "Prepariamo i documenti, organizziamo i corsi o eseguiamo i prelievi."],
  [RefreshCw, "Aggiornamento", "Teniamo tutto aggiornato nel tempo e programmiamo i controlli periodici."],
];

const Process = () => (
  <section className="border-y border-slate-200 bg-white" data-testid="home-process">
    <div className="mx-auto max-w-6xl px-6 py-20">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Come lavoriamo</p>
      <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900">Quattro passi, nessuna complicazione.</h2>
      <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map(([Icon, t, d], i) => (
          <div key={t} className="bg-white p-6">
            <div className="flex items-center justify-between"><Icon className="h-5 w-5 text-blue-600" /><span className="font-mono text-xs text-slate-400">0{i + 1}</span></div>
            <p className="mt-4 font-heading font-semibold text-slate-900">{t}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="mx-auto max-w-6xl px-6 pb-20">
    <div className="flex flex-col gap-6 rounded-2xl bg-blue-600 p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
      <div>
        <p className="font-heading text-2xl font-bold tracking-tight text-white">Vuoi mettere in regola il tuo condominio?</p>
        <p className="mt-1 text-blue-100">Privacy, formazione, acqua, energia o assistente AI: raccontaci di cosa hai bisogno.</p>
      </div>
      <Link to="/contatti" data-testid="home-final-cta-button" className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-white px-6 py-3.5 font-medium text-slate-900 transition-colors hover:bg-blue-50">Contattaci <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

const SearchBand = () => (
  <section className="bg-slate-900">
    <div className="mx-auto grid max-w-6xl gap-10 px-6 py-20 lg:grid-cols-2 lg:items-center">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-400">Banca dati informative</p>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-white">Sei un condòmino? Consulta le informative del tuo condominio.</h2>
        <p className="mt-4 max-w-md text-slate-300">Inserisci il codice fiscale del condominio per leggere e scaricare informativa privacy, accordo di contitolarità, videosorveglianza, antincendio e DUVRI.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
        {[["01", "Inserisci il codice fiscale del condominio"], ["02", "Consulta i documenti pubblicati"], ["03", "Scarica il PDF sempre aggiornato"]].map(([n, t]) => (
          <div key={n} className="flex items-center gap-4 border-l-2 border-blue-500 bg-white/5 px-5 py-4">
            <span className="font-mono text-xs text-blue-300">{n}</span><span className="text-sm text-white">{t}</span>
          </div>
        ))}
        <Link to="/informative" data-testid="home-band-informative-button" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-6 py-3.5 font-medium text-slate-900 transition-colors hover:bg-blue-50 sm:col-span-3 lg:col-span-1">
          Vai alla ricerca <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

const NormBand = () => (
  <section className="mx-auto grid max-w-6xl gap-10 px-6 py-20 lg:grid-cols-[1fr_1.2fr]" data-testid="home-normativa">
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Normativa</p>
      <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900">Le regole, spiegate in modo semplice.</h2>
      <Link to="/normativa" data-testid="home-normativa-link" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline">Leggi la sezione normativa <ArrowRight className="h-4 w-4" /></Link>
    </div>
    <div className="divide-y divide-slate-200 border-y border-slate-200">
      {[["Formazione degli amministratori", "Art. 71-bis disp. att. c.c. · D.M. 140/2014"], ["Acque destinate al consumo umano", "D.Lgs. 18/2023"], ["Prevenzione della legionella", "Linee guida nazionali 2015"], ["GDPR e Codice Privacy", "Reg. UE 2016/679 · D.Lgs. 196/2003"], ["Videosorveglianza in condominio", "Art. 1122-ter c.c. · EDPB 3/2019"], ["Sicurezza e DUVRI", "D.Lgs. 81/2008 · art. 26"], ["Intelligenza artificiale", "Reg. UE 2024/1689 (AI Act)"], ["Forniture di luce e gas", "Mercato libero · ARERA"]].map(([t, r]) => (
        <div key={t} className="flex flex-wrap items-baseline justify-between gap-2 py-4">
          <span className="font-medium text-slate-900">{t}</span><span className="font-mono text-xs text-slate-500">{r}</span>
        </div>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <SiteHeader />
      <Hero />
      <DeepSections />
      <Audience />
      <SearchBand />
      <Process />
      <NormBand />
      <FinalCta />
      <SiteFooter />
    </div>
  );
}
